// src/Components/ReleaseInfocard.js
import React from 'react';
import duplicateIcon from '../assets/icons/duplicate.svg';

const ROW_GAP = 12;
const COPY_SLOT = 28; // width/height reserved at end of each row

const Row = ({ label, value, copyable = true }) => {
  const text = (value ?? '').toString().trim();
  const display = text || '—';

  const doCopy = async () => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      // lightweight feedback
      window.clearTimeout(window.__ck_info_toast);
      const el = document.createElement('div');
      el.textContent = 'Copied!';
      el.style.cssText =
        'position:fixed;bottom:18px;left:50%;transform:translateX(-50%);background:#2a2a2a;color:#fff;padding:6px 10px;border-radius:6px;font-weight:700;z-index:9999';
      document.body.appendChild(el);
      window.__ck_info_toast = setTimeout(() => el.remove(), 900);
    } catch (_) {}
  };

  return (
    <div style={{ display: 'flex', gap: ROW_GAP, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 180, color: '#bbb', fontWeight: 700 }}>{label}</label>

      {/* Value box keeps full width; trailing copy slot is separate and fixed-size */}
      <div
        style={{
          flex: 1,
          minHeight: 28,
          display: 'flex',
          alignItems: 'center',
          background: '#202020',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 6,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={display}
      >
        {display}
      </div>

      {/* Fixed size copy slot to keep row width consistent */}
      {copyable && text ? (
        <button
          onClick={doCopy}
          title="Copy"
          style={{
            width: COPY_SLOT,
            height: COPY_SLOT,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <img
            src={duplicateIcon}
            alt="Copy"
            style={{ width: 16, height: 16, filter: 'invert(70%)', opacity: 1 }}
          />
        </button>
      ) : (
        <span style={{ width: COPY_SLOT, height: COPY_SLOT }} />
      )}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso)
      .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })
      .replaceAll('/', '.');
  } catch {
    return '';
  }
};

export default function ReleaseInfocard({ open, onClose, release }) {
  if (!open) return null;

  const isSingle =
    (release?.releaseType || '').toString().toLowerCase().includes('single');

  const fields = {
    name: release?.title || '',
    date: fmtDate(release?.releaseDate),
    isrc: release?.isrc || '',
    upc: isSingle ? (release?.trackUpc || '') : '',
    cat: isSingle ? (release?.trackCatalogNumber || '') : '',
    presave: release?.presaveLink || '',
    spotify: release?.spotifyUrl || '',
    soundcloud: release?.soundcloudUrl || '',
    disco: release?.discoUrl || '',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: '95vw',
          background: '#1e1e1e',
          borderRadius: 12,
          padding: 16,
          color: '#fff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
          Release Info
        </h2>

        {/* Body */}
        <div style={{ flex: 1 }}>
          <Row label="Release Name:" value={fields.name} />
          <Row label="Release Date:" value={fields.date} />
          <Row label="ISRC:" value={fields.isrc} />
          <Row label="UPC:" value={fields.upc} />
          <Row label="Catalogue Number:" value={fields.cat} />

          {/* Extra spacing instead of divider */}
          <div style={{ height: 10 }} />

          <Row label="Presave/Fanlink:" value={fields.presave} />
          <Row label="Spotify URL:" value={fields.spotify} />
          <Row label="Soundcloud URL:" value={fields.soundcloud} />
          <Row label="Disco URL:" value={fields.disco} />
        </div>

        {/* Footer with Close */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              background: '#2a2a2a',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}